import {
  ANNUALLY,
  MONTHLY,
  SelectableBillingPeriodTypes
} from '@constants/billingPeriods'
import { CurrencyType, PLN } from '@constants/currencies'
import { PlanRecordKeyTypes } from '@constants/plans'
import { useUserContext } from '@contexts/user'
import { convertPriceToNetto } from '@helpers/payment'
import { setLocalStorage } from '@helpers/storage'
import { getPackagePrice } from '@pages/Checkout/helpers/getPackagePrice'
import { CHECKOUT_CURRENT_STEP } from '@pages/Checkout/routes/constants'
import { PAYMENTS } from '@routes/path'
import { CheckoutRoutesTypes } from '@routes/path/payments'
import { useGetPackages } from '@services/payments/useGetPackages'
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

export type CheckoutProviderProps = {
  children: ReactNode
}

export type CheckoutProviderValue = {
  chosenBillingPeriod: SelectableBillingPeriodTypes
  setBillingPeriod: Dispatch<SetStateAction<SelectableBillingPeriodTypes>>
  isLastStep: boolean
  isFirstOrLastStep: boolean
  goToStep: (stepRoute: CheckoutRoutesTypes) => void
  goToPreviousStep: (stepRoute: string) => void
  steps: Array<{ path: CheckoutRoutesTypes; label: string }>
  isCurrentPlan: (planRecordKey?: string) => boolean
  accountCurrency: CurrencyType
  getPlanPrice: (planRecordKey: PlanRecordKeyTypes) => number
  packagesIsLoading: boolean
  getAnnualSavingPrice: (planRecordKey: PlanRecordKeyTypes) => number
}

const CheckoutContext = createContext<CheckoutProviderValue | null>(null)

export const CheckoutProvider: FC<CheckoutProviderProps> = ({ children }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const {
    billingPeriod: accountPeriod,
    planRecordKey: accountPlanRecordKey,
    accountCurrency
  } = useUserContext()

  const steps = useMemo(
    () => [
      {
        path: PAYMENTS.CHECKOUT.PLANS,
        label: t('checkout.wrapper.steps.plans')
      },
      {
        path: PAYMENTS.CHECKOUT.CONFIGURE,
        label: t('checkout.wrapper.steps.configure')
      },
      {
        path: PAYMENTS.CHECKOUT.PAYMENT,
        label: t('checkout.wrapper.steps.payment')
      },
      {
        path: PAYMENTS.CHECKOUT.CONFIRMATION,
        label: t('checkout.wrapper.steps.confirmation')
      }
    ],
    [t]
  )

  const [chosenBillingPeriod, setBillingPeriod] =
    useState<SelectableBillingPeriodTypes>(
      accountPeriod === ANNUALLY ? ANNUALLY : MONTHLY
    )

  useEffect(() => {
    const matchedStep = steps.find(step => step.path === location.pathname)

    if (!matchedStep) {
      navigate(PAYMENTS.CHECKOUT.PLANS, { replace: true })
    } else {
      const currentStepIndex = steps.findIndex(
        step => step.path === location.pathname
      )

      const storedStep = localStorage.getItem(CHECKOUT_CURRENT_STEP)
      const storedStepIndex = steps.findIndex(step => step.path === storedStep)

      if (storedStepIndex >= currentStepIndex || !storedStep) {
        setLocalStorage(CHECKOUT_CURRENT_STEP, matchedStep.path)
      }
    }
  }, [location.pathname, steps, navigate])

  const { data: packages, isLoading: packagesIsLoading } = useGetPackages()

  const getPlanPrice = useCallback(
    (
      planRecordKey: PlanRecordKeyTypes,
      billingPeriod: SelectableBillingPeriodTypes = chosenBillingPeriod
    ) => {
      const getGrossPrice = getPackagePrice({
        packages: packages ?? [],
        periodToFind: billingPeriod,
        currencyToFind: accountCurrency,
        packageRecordKey: planRecordKey
      })

      return accountCurrency === PLN
        ? convertPriceToNetto(getGrossPrice)
        : getGrossPrice
    },
    [packages, chosenBillingPeriod, accountCurrency]
  )

  const getAnnualSavingPrice = useCallback(
    (planRecordKey: PlanRecordKeyTypes) => {
      const priceMonthly = getPlanPrice(planRecordKey, MONTHLY)

      const priceAnually = getPlanPrice(planRecordKey, ANNUALLY)

      return (priceMonthly - priceAnually) * 12
    },
    [getPlanPrice]
  )

  const isCurrentPlan = useCallback(
    (planRecordKey?: string) =>
      planRecordKey === accountPlanRecordKey &&
      accountPeriod === chosenBillingPeriod,
    [accountPlanRecordKey, accountPeriod, chosenBillingPeriod]
  )

  const [step] = useState(PAYMENTS.CHECKOUT.PLANS)

  const isLastStep = location.pathname === PAYMENTS.CHECKOUT.CONFIRMATION
  const isFirstStep = location.pathname === PAYMENTS.CHECKOUT.PLANS
  const isFirstOrLastStep = isFirstStep || isLastStep

  const goToStep = useCallback(
    (stepRoute: CheckoutRoutesTypes) => {
      navigate(stepRoute)
      setLocalStorage(CHECKOUT_CURRENT_STEP, stepRoute)
    },
    [navigate]
  )

  const goToPreviousStep = useCallback(
    (stepRoute: string) => {
      let newStep: CheckoutRoutesTypes = PAYMENTS.CHECKOUT.PLANS

      switch (stepRoute) {
        case PAYMENTS.CHECKOUT.CONFIGURE:
          newStep = PAYMENTS.CHECKOUT.PLANS
          break
        case PAYMENTS.CHECKOUT.PAYMENT:
          newStep = PAYMENTS.CHECKOUT.CONFIGURE
          break
        default:
          newStep = PAYMENTS.CHECKOUT.PLANS
      }

      navigate(newStep)
      setLocalStorage(CHECKOUT_CURRENT_STEP, newStep)
    },
    [navigate]
  )

  const value = useMemo(
    () => ({
      chosenBillingPeriod,
      setBillingPeriod,
      step,
      isLastStep,
      isCurrentPlan,
      accountCurrency,
      getPlanPrice,
      packagesIsLoading,
      getAnnualSavingPrice,
      isFirstOrLastStep,
      goToStep,
      goToPreviousStep,
      steps
    }),
    [
      chosenBillingPeriod,
      setBillingPeriod,
      step,
      isLastStep,
      isCurrentPlan,
      accountCurrency,
      getPlanPrice,
      packagesIsLoading,
      getAnnualSavingPrice,
      isFirstOrLastStep,
      goToStep,
      goToPreviousStep,
      steps
    ]
  )

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  )
}

export const useCheckoutContext = () => {
  const value = useContext(CheckoutContext)

  if (!value) {
    throw new Error('useCheckoutContext must be used inside CheckoutProvider')
  }

  return value
}
